import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, ArrowUpDown } from 'lucide-react';
import { FilterOptions } from '../types';

interface FilterControlsProps {
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  languages: string[];
  isChannelContext?: boolean; // Add this line
}

const languageNames: { [key: string]: string } = {
  en: 'English (EN)',
  es: 'Español (ES)',
  fr: 'Français (FR)',
  tl: 'Tagalog (TL)',
  ru: 'Русский (RU)',
  zh: '中文 (ZH)',
  ko: '한국어 (KO)',
  th: 'ไทย (TH)',
  pt: 'Português (PT)',
  vi: 'Tiếng Việt (VI)',
  id: 'Bahasa Indonesia (ID)',
  gl: 'Galego (GL)',
  fil: 'Filipino (FIL)',
  ht: 'Haitian Creole (HT)',
  de: 'Deutsch (DE)',
  af: 'Afrikaans (AF)',
  my: 'မြန်မာစာ (MY)', // Burmese
  ca: 'Català (CA)',
  da: 'Dansk (DA)',
  nl: 'Nederlands (NL)',
  it: 'Italiano (IT)',
  lus: 'Mizo (LUS)',
  km: 'ភាសាខ្មែរ (KM)',
  su: 'Basa Sunda (SU)',
  'ja-la': 'Japanese (Latin)',
  nn: 'Nynorsk (NN)',
  sv: 'Svenska (SV)',
  nb: 'Bokmål (NB)',
  cs: 'Čeština (CS)',
  jv: 'Basa Jawa (JV)',
  fi: 'Suomi (FI)',
  tn: 'Setswana (TN)',
  ss: 'siSwati (SS)',
  ms: 'Bahasa Melayu (MS)',
  ha: 'Hausa (HA)',
  ln: 'Lingála (LN)',
  sn: 'chiShona (SN)',
  ro: 'Română (RO)',
  st: 'Sesotho (ST)',
  ng: 'Ndonga (NG)',
  ilo: 'Iloko (ILO)',
  bik: 'Bikol (BIK)',
  ny: 'Chichewa (NY)',
  ja: '日本語 (JA)',
  'hi-la': 'Hindi (Latin)',
  ar: 'العربية (AR)',
  // Add more mappings if needed
};

export const FilterControls: React.FC<FilterControlsProps> = ({
  filters,
  onFiltersChange,
  languages,
  isChannelContext = false // Add this line and default value
}) => {
  const sortOptions = [
    { value: 'views', label: 'Views' },
    { value: 'duration', label: 'Duration' },
    { value: 'published_at', label: 'Published Date' },
    { value: 'rating', label: 'Rating' },
    { value: 'like_count', label: 'Likes' }
  ];

  const durationOptions = [
    { label: 'All Durations', value: '' },
    { label: 'Under 1 hour', value: 'under1h' },
    { label: '1–2 hours', value: '1to2h' },
    { label: '2–4 hours', value: '2to4h' },
    { label: 'Over 4 hours', value: 'over4h' },
  ];

  const handleDurationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    let minDuration: number | undefined = undefined;
    let maxDuration: number | undefined = undefined;
    if (val === 'under1h') maxDuration = 3600;
    else if (val === '1to2h') { minDuration = 3600; maxDuration = 7200; }
    else if (val === '2to4h') { minDuration = 7200; maxDuration = 14400; }
    else if (val === 'over4h') minDuration = 14400;

    // When a duration filter is applied, automatically set sort order to ASC
    // so users see shortest durations first (most relevant)
    const newSortOrder = val ? 'asc' : filters.sortOrder;

    onFiltersChange({
      ...filters,
      minDuration,
      maxDuration,
      sortOrder: newSortOrder
    });
  };

  // Determine which option is selected
  let selectedDuration = '';
  if (filters.minDuration === undefined && filters.maxDuration === 3600) selectedDuration = 'under1h';
  else if (filters.minDuration === 3600 && filters.maxDuration === 7200) selectedDuration = '1to2h';
  else if (filters.minDuration === 7200 && filters.maxDuration === 14400) selectedDuration = '2to4h';
  else if (filters.minDuration === 14400 && filters.maxDuration === undefined) selectedDuration = 'over4h';

  // Get display names for active filters
  const getLanguageDisplay = () => {
    if (!filters.language) return null;
    const lcLangCode = filters.language.toLowerCase();
    return languageNames[lcLangCode] || filters.language.toUpperCase();
  };

  const getSortDisplay = () => {
    const sortOption = sortOptions.find(opt => opt.value === filters.sortBy);
    return sortOption ? `Sort by ${sortOption.label}` : null;
  };

  const getDurationDisplay = () => {
    if (selectedDuration === 'under1h') return 'Under 1 hour';
    if (selectedDuration === '1to2h') return '1–2 hours';
    if (selectedDuration === '2to4h') return '2–4 hours';
    if (selectedDuration === 'over4h') return 'Over 4 hours';
    return null;
  };

  const activeFilters = [
    getLanguageDisplay(),
    getSortDisplay(),
    getDurationDisplay()
  ].filter(Boolean);

  return (
    <div className="flex flex-wrap gap-4 items-center">
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <span className="text-sm text-gray-700 dark:text-gray-300">Filters:</span>
      </div>

      {/* Language Filter */}
      <select
        value={filters.language}
        onChange={(e) => onFiltersChange({ ...filters, language: e.target.value })}
        className={`px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isChannelContext ? 'opacity-70 cursor-not-allowed' : ''
          }`}
        disabled={isChannelContext}
      >
        <option value="">All Languages</option>
        {languages.map(langCode => {
          const lcLangCode = langCode.toLowerCase();
          const displayName = languageNames[lcLangCode] || `${langCode.charAt(0).toUpperCase() + langCode.slice(1)} (${langCode.toUpperCase()})`;
          return (
            <option key={langCode} value={langCode}>
              {displayName}
            </option>
          );
        })}
      </select>

      {/* Sort by Filter */}
      <div className="flex items-center gap-2">
        <ArrowUpDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <select
          value={filters.sortBy}
          onChange={(e) => onFiltersChange({ ...filters, sortBy: e.target.value as FilterOptions['sortBy'] })}
          className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          {sortOptions.map(option => (
            <option key={option.value} value={option.value}>
              Sort by {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Duration Filter - Only show when "Sort by Duration" is selected */}
      {filters.sortBy === 'duration' && (
        <select
          value={selectedDuration}
          onChange={handleDurationChange}
          className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          {durationOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}

      {/* Sort Order Button */}
      <button
        onClick={() => onFiltersChange({
          ...filters,
          sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc'
        })}
        className={`px-3 py-2 border rounded-lg text-sm transition-colors duration-200 ${selectedDuration && filters.sortOrder === 'asc'
          ? 'bg-purple-100 dark:bg-purple-900 border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300'
          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        title={selectedDuration && filters.sortOrder === 'asc'
          ? 'Auto-sorted: Shortest durations first'
          : `Sort order: ${filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}`
        }
      >
        {filters.sortOrder === 'asc' ? '↑' : '↓'}
      </button>
    </div>
  );
};

// Alphabetical/Numberical Filter Component
export const AlphabeticalFilter: React.FC<{
  startsWith?: string;
  onStartsWithChange: (char: string | undefined) => void;
}> = ({ startsWith, onStartsWithChange }) => {
  const [searchParams] = useSearchParams();
  // Only show # for numbers, then A-Z
  const characters = ['#', ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];

  const createUrl = (char: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    // Always remove 'series' parameter when clicking A-Z links
    params.delete('series');
    // Also remove 'search' parameter
    params.delete('search');
    // Clear 'genre' parameter when clicking A-Z links
    params.delete('genre');
    if (char) {
      params.set('startsWith', char);
    } else {
      params.delete('startsWith');
    }
    return `/?${params.toString()}`;
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Browse by:</span>
      </div>
      {/* Horizontal scrolling container for small screens, flex-wrap for larger screens */}
      <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent sm:flex-wrap sm:overflow-x-visible">
        {characters.map(char => {
          const isActive = startsWith === char;
          const url = createUrl(isActive ? undefined : char);
          return (
            <Link
              key={char}
              to={url}
              onClick={(e) => {
                // Still call the handler for immediate state update
                onStartsWithChange(isActive ? undefined : char);
              }}
              className={`px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 inline-block text-center whitespace-nowrap flex-shrink-0 ${isActive
                ? 'bg-purple-600 dark:bg-purple-500 text-white'
                : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              title={char === '#' ? 'Titles starting with numbers (0-9)' : `Titles starting with "${char}"`}
            >
              {char}
            </Link>
          );
        })}
      </div>
    </div>
  );
};